import type { SetupRepositoryPort } from "@/application/records/ports/SetupRepositoryPort";
import { SetupDomainService } from "@/domain/records/services/SetupDomainService";
import { ValidationService } from "@/domain/records/services/ValidationService";
import {
  SetupConfig,
  type SetupConfigUpdatableField,
} from "@/domain/records/value-objects/SetupConfig";
import type { RecordId, ReportType } from "@/domain/records/types";

/**
 * Use Case for managing record setup configuration
 * Clean Architecture - Application Layer
 */
export class SetupRecordUseCase {
  constructor(
    private readonly setupRepository: SetupRepositoryPort,
    private readonly setupDomainService: SetupDomainService
  ) {}

  /**
   * Load setup configuration for a record
   */
  async loadSetup(
    recordId: RecordId,
    version?: number
  ): Promise<{
    config: SetupConfig;
    isLoading: boolean;
    error?: string;
  }> {
    try {
      const setupData = await this.setupRepository.getByRecordId(
        recordId,
        version
      );
      const availableVersions = await this.setupRepository.getAvailableVersions(
        recordId
      );

      let config: SetupConfig;

      if (setupData) {
        // Initialize from existing data
        config = this.setupDomainService.initializeFromData(
          recordId,
          setupData,
          availableVersions
        );
      } else {
        // Create default configuration
        const isTextFile = await this.checkIfTextFile(recordId);
        config = this.setupDomainService.createDefaultConfig(
          recordId,
          isTextFile
        );
      }

      return {
        config,
        isLoading: false,
      };
    } catch (error) {
      console.error("Failed to load setup:", error);

      // Return default config on error
      const defaultConfig =
        this.setupDomainService.createDefaultConfig(recordId);

      return {
        config: defaultConfig,
        isLoading: false,
        error: error instanceof Error ? error.message : "Failed to load setup",
      };
    }
  }

  /**
   * Save setup configuration
   */
  async saveSetup(config: SetupConfig): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      // For partial updates (saving individual fields), skip strict validation
      // Full validation is done before report generation
      // Only validate if it's a complete form submission (has report types and goals)
      const isCompleteSubmission =
        config.reportTypes.length > 0 && config.goals?.trim().length > 0;

      if (isCompleteSubmission) {
        const validation = ValidationService.validateCompleteSetupForm(config);
        if (!validation.isValid) {
          return {
            success: false,
            error: validation.errors.join(", "),
          };
        }
      }

      // Save to repository
      await this.setupRepository.save(config);

      return { success: true };
    } catch (error) {
      console.error("Failed to save setup:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to save setup",
      };
    }
  }

  /**
   * Update setup field
   */
  updateSetupField<TField extends SetupConfigUpdatableField>(
    config: SetupConfig,
    field: TField,
    value: SetupConfig[TField]
  ): SetupConfig {
    return this.setupDomainService.updateField(config, field, value);
  }

  /**
   * Toggle report type
   */
  toggleReportType(config: SetupConfig, reportType: ReportType): SetupConfig {
    return this.setupDomainService.toggleReportType(config, reportType);
  }

  /**
   * Add respondent
   */
  addRespondent(config: SetupConfig): SetupConfig {
    return this.setupDomainService.addRespondent(config);
  }

  /**
   * Remove respondent
   */
  removeRespondent(config: SetupConfig, index: number): SetupConfig {
    return this.setupDomainService.removeRespondent(config, index);
  }

  /**
   * Update respondent
   */
  updateRespondent(
    config: SetupConfig,
    index: number,
    name: string
  ): SetupConfig {
    return this.setupDomainService.updateRespondent(config, index, name);
  }

  /**
   * Validate setup configuration
   */
  validateSetup(config: SetupConfig) {
    return ValidationService.validateCompleteSetupForm(config);
  }

  /**
   * Check if setup can be submitted
   */
  canSubmitSetup(config: SetupConfig): boolean {
    return this.setupDomainService.canSubmit(config);
  }

  /**
   * Check if record is a text file
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private async checkIfTextFile(_recordId: RecordId): Promise<boolean> {
    try {
      // This would be implemented in the repository
      // For now, return false as default
      return false;
    } catch {
      return false;
    }
  }
}
